import { apiClient } from "../client";

export interface DepartmentSummary {
  id: number;
  name: string;
  systemCode: string;
  healthStatus: {
    name: string;
    color: string | null;
  } | null;
  totalAssets: number;
  totalValue: number;
  complianceStatus: {
    name: string;
    description: string | null;
  } | null;
  openIssues: number;
  createdAt: string;
  updatedAt: string;
}

export interface DepartmentAnalytics {
  totalAssets: number;
  totalValue: number;
  healthStatusBreakdown: Array<{
    healthStatus: string;
    color: string | null;
    count: number;
  }>;
  complianceStatusBreakdown: Array<{
    complianceStatus: string;
    count: number;
  }>;
  openIssues: number;
  resolvedIssues: number;
  assetsByState: Array<{
    state: string;
    count: number;
  }>;
}

export interface Department {
  id: number;
  name: string;
  systemCode: string;
  description: string | null;
  siteId: number;
  organizationId: number;
  createdAt: string;
  updatedAt: string;
}

export interface DepartmentDetail {
  department: Department;
  analytics: DepartmentAnalytics;
}

export interface Asset {
  id: number;
  assetTagId: string;
  assetImage?: string;
  name: string;
  description: string | null;
  brand: string | null;
  model: string | null;
  purchasePrice: number | null;
  currentValue?: number | null;
  serialNumber: string | null;
  purchaseDate: string | null;
  purchaseCost: string | null;
  warrantyExpiry: string | null;
  siteId: number;
  organizationId: number;
  categoryId: number | null;
  departmentId: number | null;
  locationId: number | null;
  healthStatusId: number | null;
  complianceStatusId: number | null;
  assetStateId: number | null;
  criticalityId: number | null;
  assignedToId: number | null;
  maintainedById: number | null;
  maintenanceUpToDate: boolean;
  lastMaintenanceDate: string | null;
  nextMaintenanceDate: string | null;
  maintenanceScheduleFrequency: string | null;
  maintenanceScheduleConfig: {
    frequency: string;
    customDays?: number;
    startDate?: string;
    endDate?: string;
    neverEnding?: boolean;
    excludeWeekends?: boolean;
  } | null;
  failureProbability: number | null;
  createdBy: number;
  updatedBy: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  category?: { id: number; name: string };
  healthStatus?: { id: number; name: string; color?: string };
  complianceStatus?: { id: number; name: string };
  assetState?: { id: number; name: string };
  department?: { id: number; name: string };
  location?: { id: number; name: string };
  criticality?: { id: number; name: string };
  assignedTo?: {
    id: number;
    name: string;
    email: string;
    profilePicture?: string;
  };
  maintainedBy?: {
    id: number;
    name: string;
    email: string;
    profilePicture?: string | null;
  };
  creator: {
    id: number;
    email: string;
    googleId: string | null;
    name: string;
    profilePicture: string | null;
  };
  updater?: { id: number; name: string; email: string };
}

export interface AuditLog {
  id: number;
  date: string;
  assetId: number;
  assetName: string;
  user: {
    id: number;
    name: string;
    email: string;
    profilePicture: string | null;
  };
  actionType: string;
  description: string;
  oldValue: string | null;
  newValue: string | null;
}

export interface IssueLog {
  id: number;
  name: string;
  description: string | null;
  imagePaths: string[];
  reportedDate: string;
  status: "REPORTED" | "IN_PROGRESS" | "RESOLVED";
  asset: {
    id: number;
    name: string;
    assetTagId: string;
    criticality: { id: number; name: string } | null;
    location: { id: number; name: string } | null;
    maintainedBy: {
      id: number;
      name: string;
      email: string;
      profilePicture: string | null;
    } | null;
  };
  reporter: {
    id: number;
    name: string;
    email: string;
    profilePicture: string | null;
  } | null;
  createdAt: string;
  updatedAt: string;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface GetDepartmentsSummaryResponse {
  message: string;
  departments: DepartmentSummary[];
  pagination: PaginationMeta;
}

export interface GetDepartmentAssetsResponse {
  message: string;
  department: {
    id: number;
    name: string;
    systemCode: string;
  };
  assets: Asset[];
  pagination: PaginationMeta;
}

export interface GetDepartmentAuditLogsResponse {
  message: string;
  department: {
    id: number;
    name: string;
    systemCode: string;
  };
  logs: AuditLog[];
  pagination: PaginationMeta;
}

export interface GetDepartmentAssetAuditLogsResponse {
  message: string;
  department: {
    id: number;
    name: string;
    systemCode: string;
  };
  asset: {
    id: number;
    name: string;
    assetTagId: string;
  };
  logs: AuditLog[];
  pagination: PaginationMeta;
}

export interface GetDepartmentIssuesResponse {
  message: string;
  department: {
    id: number;
    name: string;
    systemCode: string;
  };
  issues: IssueLog[];
  pagination: PaginationMeta;
}

export interface GetDepartmentAssetIssuesResponse {
  message: string;
  department: {
    id: number;
    name: string;
    systemCode: string;
  };
  asset: {
    id: number;
    name: string;
    assetTagId: string;
  };
  issues: IssueLog[];
  pagination: PaginationMeta;
}

export interface DepartmentFilterParams {
  page?: number;
  limit?: number;
  search?: string;
  actionType?: string;
  status?: "REPORTED" | "IN_PROGRESS" | "RESOLVED";
}

export interface ApiError {
  message: string | string[];
  error?: string;
  statusCode?: number;
}

export interface AxiosErrorResponse {
  response?: {
    data: ApiError;
  };
  message: string;
}

export class DepartmentService {
  /**
   * Get all departments with summary analytics
   * Includes: name, health, totalAssets, totalValue, compliance, openIssues
   */
  static async getDepartmentsSummary(
    organizationId: number,
    siteId: number,
    params?: DepartmentFilterParams,
  ): Promise<GetDepartmentsSummaryResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.search) queryParams.append("search", params.search);

    const queryString = queryParams.toString();
    const url = queryString
      ? `/organizations/${organizationId}/sites/${siteId}/departments?${queryString}`
      : `/organizations/${organizationId}/sites/${siteId}/departments`;

    return await apiClient.get<GetDepartmentsSummaryResponse>(url);
  }

  /**
   * Get detailed analytics for a specific department
   * Includes all summary data plus breakdown of health, compliance, asset states, and issues
   */
  static async getDepartmentDetails(
    organizationId: number,
    siteId: number,
    departmentId: number,
  ): Promise<DepartmentDetail> {
    return await apiClient.get<DepartmentDetail>(
      `/organizations/${organizationId}/sites/${siteId}/departments/${departmentId}`,
    );
  }

  /**
   * Get all assets in a specific department
   * Returns assets in same format as Asset listing
   */
  static async getDepartmentAssets(
    organizationId: number,
    siteId: number,
    departmentId: number,
    params?: DepartmentFilterParams,
  ): Promise<GetDepartmentAssetsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.search) queryParams.append("search", params.search);

    const queryString = queryParams.toString();
    const url = queryString
      ? `/organizations/${organizationId}/sites/${siteId}/departments/${departmentId}/assets?${queryString}`
      : `/organizations/${organizationId}/sites/${siteId}/departments/${departmentId}/assets`;

    return await apiClient.get<GetDepartmentAssetsResponse>(url);
  }

  /**
   * Get audit logs for a specific asset in a department
   * Includes pagination and search/filter support
   */
  static async getDepartmentAssetAuditLogs(
    organizationId: number,
    siteId: number,
    departmentId: number,
    assetId: number,
    params?: DepartmentFilterParams,
  ): Promise<GetDepartmentAssetAuditLogsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.search) queryParams.append("search", params.search);
    if (params?.actionType) queryParams.append("actionType", params.actionType);

    const queryString = queryParams.toString();
    const url = queryString
      ? `/organizations/${organizationId}/sites/${siteId}/departments/${departmentId}/assets/${assetId}/audit-logs?${queryString}`
      : `/organizations/${organizationId}/sites/${siteId}/departments/${departmentId}/assets/${assetId}/audit-logs`;

    return await apiClient.get<GetDepartmentAssetAuditLogsResponse>(url);
  }

  /**
   * Get issues for a specific asset in a department
   * Includes pagination, search, and status filter support
   */
  static async getDepartmentAssetIssues(
    organizationId: number,
    siteId: number,
    departmentId: number,
    assetId: number,
    params?: DepartmentFilterParams,
  ): Promise<GetDepartmentAssetIssuesResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.search) queryParams.append("search", params.search);
    if (params?.status) queryParams.append("status", params.status);

    const queryString = queryParams.toString();
    const url = queryString
      ? `/organizations/${organizationId}/sites/${siteId}/departments/${departmentId}/assets/${assetId}/issues?${queryString}`
      : `/organizations/${organizationId}/sites/${siteId}/departments/${departmentId}/assets/${assetId}/issues`;

    return await apiClient.get<GetDepartmentAssetIssuesResponse>(url);
  }

  static async getDepartmentAuditLogs(
    organizationId: number,
    siteId: number,
    departmentId: number,
    params?: DepartmentFilterParams,
  ): Promise<GetDepartmentAuditLogsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.search) queryParams.append("search", params.search);
    if (params?.actionType) queryParams.append("actionType", params.actionType);

    const queryString = queryParams.toString();
    const url = queryString
      ? `/organizations/${organizationId}/sites/${siteId}/departments/${departmentId}/audit-logs?${queryString}`
      : `/organizations/${organizationId}/sites/${siteId}/departments/${departmentId}/audit-logs`;

    return await apiClient.get<GetDepartmentAuditLogsResponse>(url);
  }

  /**
   * Get all issues for assets in a specific department
   * Includes pagination, search, and status filter support
   */
  static async getDepartmentIssues(
    organizationId: number,
    siteId: number,
    departmentId: number,
    params?: DepartmentFilterParams,
  ): Promise<GetDepartmentIssuesResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.search) queryParams.append("search", params.search);
    if (params?.status) queryParams.append("status", params.status);

    const queryString = queryParams.toString();
    const url = queryString
      ? `/organizations/${organizationId}/sites/${siteId}/departments/${departmentId}/issues?${queryString}`
      : `/organizations/${organizationId}/sites/${siteId}/departments/${departmentId}/issues`;

    return await apiClient.get<GetDepartmentIssuesResponse>(url);
  }
}
